import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient, formatApiErrorDetail } from "../lib/api";
import { AppHeader } from "../components/AppHeader";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import { Button } from "../components/ui/button";
import { Search, Phone, Truck, Plus, KeyRound, Copy, Link2, BadgeCheck } from "lucide-react";
import { ContactActions } from "../components/ContactActions";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";

export default function Suppliers() {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myCode, setMyCode] = useState("");
  const [linkOpen, setLinkOpen] = useState(false);
  const [codeInput, setCodeInput] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [linking, setLinking] = useState(false);

  const loadSuppliers = () => {
    setLoading(true);
    apiClient.get(`/suppliers${q ? `?q=${encodeURIComponent(q)}` : ""}`)
      .then((r) => setItems(r.data)).catch(() => setItems([])).finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(loadSuppliers, 200);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    apiClient.get("/me/supplier-code").then((r) => setMyCode(r.data.code)).catch(() => {});
  }, []);

  const copyCode = async () => {
    try { await navigator.clipboard.writeText(myCode); toast.success("Code copied"); }
    catch { toast.error("Copy failed"); }
  };

  const lookupCode = async () => {
    const c = codeInput.trim().toUpperCase();
    if (c.length < 4) { toast.error("Enter a valid code"); return; }
    setPreviewLoading(true);
    setPreview(null);
    try {
      const { data } = await apiClient.get(`/supplier-codes/${c}`);
      setPreview(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Invalid code");
    } finally { setPreviewLoading(false); }
  };

  const confirmLink = async () => {
    setLinking(true);
    try {
      const { data } = await apiClient.post("/suppliers/link-by-code", { code: codeInput.trim().toUpperCase() });
      toast.success(`Linked: ${data.name}`);
      setLinkOpen(false); setCodeInput(""); setPreview(null);
      loadSuppliers();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to link");
    } finally { setLinking(false); }
  };

  return (
    <>
      <AppHeader title="Suppliers" subtitle={`${items.length} ${items.length === 1 ? "supplier" : "suppliers"}`} />
      <div className="px-4 pt-4 pb-28 space-y-3">
        {/* My Supplier Code card */}
        {myCode && (
          <div data-testid="my-supplier-code-card" className="bg-gradient-to-br from-emerald-50 to-white border border-emerald-200 rounded-2xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white grid place-items-center shrink-0">
                <BadgeCheck className="w-5 h-5" strokeWidth={2.5} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold text-emerald-800 uppercase tracking-wider">Your supplier code</div>
                <div className="text-sm text-slate-600 mt-0.5">Share with buyers so they can add you & send orders inside the app.</div>
                <div className="mt-2 flex items-center gap-2">
                  <code data-testid="my-supplier-code" className="text-lg font-bold tracking-widest text-emerald-800 bg-white border border-emerald-200 rounded-lg px-3 py-1.5 flex-1 text-center">
                    {myCode}
                  </code>
                  <Button onClick={copyCode} data-testid="copy-my-code-btn" size="icon" variant="outline" className="h-10 w-10 shrink-0 rounded-lg">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="suppliers-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search supplier"
              className="h-12 pl-12 rounded-xl text-base bg-white"
            />
          </div>
          <Link to="/suppliers/new" data-testid="suppliers-add-btn"
            className="h-12 px-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-1 font-semibold text-sm">
            <Plus className="w-4 h-4" /> Add
          </Link>
        </div>

        <Dialog open={linkOpen} onOpenChange={(o) => { setLinkOpen(o); if (!o) { setCodeInput(""); setPreview(null); } }}>
          <DialogTrigger asChild>
            <Button
              variant="outline"
              data-testid="link-supplier-btn"
              className="w-full h-11 rounded-xl border-indigo-200 text-indigo-700 hover:bg-indigo-50 font-semibold gap-2"
            >
              <KeyRound className="w-4 h-4" /> Add supplier by invite code
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-sm rounded-2xl">
            <DialogHeader>
              <DialogTitle>Link a supplier</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-600">Ask your supplier for their 8-character KonexoCRM code and paste it below.</p>
            <div className="flex gap-2">
              <Input
                data-testid="link-code-input"
                value={codeInput}
                onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                placeholder="e.g., HCQS4JMR"
                className="h-12 rounded-xl text-base tracking-widest text-center font-mono uppercase"
                maxLength={12}
              />
              <Button
                onClick={lookupCode}
                disabled={previewLoading}
                data-testid="link-code-lookup-btn"
                className="h-12 px-4 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold"
              >
                Check
              </Button>
            </div>
            {preview && (
              <div data-testid="link-code-preview" className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                <div className="text-xs font-semibold text-emerald-800 uppercase tracking-wider mb-1">Found on KonexoCRM</div>
                <div className="font-bold text-slate-900">{preview.business_name || preview.name}</div>
                {preview.business_name && <div className="text-xs text-slate-500">{preview.name}</div>}
              </div>
            )}
            <DialogFooter>
              <Button
                onClick={confirmLink}
                disabled={!preview || linking}
                data-testid="link-code-confirm-btn"
                className="w-full h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold gap-2"
              >
                <Link2 className="w-4 h-4" /> {linking ? "Linking..." : "Add as supplier"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {loading ? (
          <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-20 rounded-2xl" />)}</div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-dashed border-slate-200">
            <div className="w-14 h-14 rounded-full bg-slate-100 mx-auto grid place-items-center mb-3">
              <Truck className="w-7 h-7 text-slate-400" strokeWidth={2.5} />
            </div>
            <p className="text-slate-700 font-semibold mb-1">{q ? "No matches" : "No suppliers yet"}</p>
            <p className="text-sm text-slate-500">{q ? "Try a different search" : "Tap + to add your first supplier"}</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="supplier-list">
            {items.map((s) => (
              <Link key={s.id} to={`/suppliers/${s.id}`} data-testid={`supplier-card-${s.id}`}
                className="flex items-center gap-3 bg-white border border-slate-200 rounded-2xl p-3 active:bg-slate-50">
                <div className="w-11 h-11 rounded-full bg-indigo-100 text-indigo-700 grid place-items-center font-bold text-base shrink-0">
                  {s.name.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-900 truncate flex items-center gap-1.5">
                    {s.name}
                    {s.linked_owner_id && (
                      <span data-testid={`supplier-on-app-${s.id}`} title="On KonexoCRM" className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-full">
                        <BadgeCheck className="w-3 h-3" /> On App
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-slate-500 flex items-center gap-1.5">
                    <Phone className="w-3.5 h-3.5" /> {s.phone || "—"}
                  </div>
                </div>
                {s.phone && (
                  <ContactActions phone={s.phone} name={s.name} waMessage={`Hi ${s.name} 🙏, quick enquiry for you.`} />
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
